/* stylelint-disable a11y/media-prefers-reduced-motion */

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

const TRANSITION_DURATION_BASE = 0.2;
const TRANSITION_DURATION = `${TRANSITION_DURATION_BASE}s`;

/**
 * DiffTabContainer stretches the diff tab panel content to the page's width.
 */
export const diffTabStyle = xcss({
  width: '100%',
});

/**
 * diffLoaderStyle fills the remaining space in the diff tab panel outside of the file tree.
 */
export const diffLoaderFullLayoutStyle = xcss({
  minWidth: token('space.0'),
  minHeight: '100%',
  width: '100%',
});

/**
 * SidebarContainer controls the position of the sidebar and its contents.
 * It sticks to underneath the diff header.
 */
export const SidebarContainer = styled.section({
  display: 'flex',
  position: 'sticky',
  marginRight: token('space.500'),
  maxHeight: 'calc(100vh - var(--pr-compact-header-bottom))',
  top: `var(--pr-compact-header-bottom)`,
});

/**
 * Sidebar is the container for the file tree and controls.
 * This is what gets resized when the SidebarControls are dragged.
 */
export const Sidebar = styled.div<{ width: number }>(
  {
    height: '100%',
    transition: `background ${TRANSITION_DURATION} ease-out`,
  },
  ({ width }) => ({
    width: `${width}px`,
  })
);

/**
 * SidebarContent is a wrapper around the file tree needed to make it scrollable.
 */
export const SidebarContent = styled.div({
  // Added so the file tree doesn't extend past the container (esp. for diffs with large num of files)
  position: 'relative',
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: 'calc(100vh - var(--pr-compact-header-bottom))',
});

/**
 * SidebarControls is the draggable area to resize the sidebar. Contained within the controls are:
 * - Shadow: a gray bar to indicate a grabbable area
 * - CollapseButton: a button to collapse the sidebar
 */
export const SidebarControls = styled.div<{ isSidebarCollapsed: boolean }>(
  {
    position: 'absolute',
    insetBlockEnd: token('space.0'),
    insetBlockStart: token('space.0'),
    insetInlineStart: '100%',
    paddingLeft: token('space.150'),
    paddingRight: token('space.150'),
    zIndex: layers.navigation(),
    userSelect: 'none',
  },
  ({ isSidebarCollapsed }) => ({
    cursor: isSidebarCollapsed ? 'default' : 'ew-resize',
    ':hover, :focus, :active': {
      '> div': {
        transition: TRANSITION_DURATION,
        background: isSidebarCollapsed
          ? undefined
          : token('color.border.focused'),
        '> button': {
          transition: TRANSITION_DURATION,
          opacity: 1,
        },
      },
    },
  })
);

/**
 * shadowStyles is a gray bar to indicate a grabbable area.
 */
export const shadowStyles = xcss({
  width: token('space.050'),
  position: 'absolute',
  top: token('space.0'),
  bottom: token('space.0'),
  background: token('color.border'),
});

// TODO consider replacing this with the IconButton once we've updated to @atlaskit/button@17
/**
 * CollapseButton is a button that collapses the sidebar.
 */
export const CollapseButton = styled.button<{ isSidebarCollapsed: boolean }>(
  {
    width: token('space.300'),
    height: token('space.300'),
    borderRadius: token('border.radius.circle', '50%'),
    padding: token('space.0'),
    position: 'absolute',
    insetBlockStart: token('space.400'),
    insetInlineStart: token('space.025'),
    color: token('color.text.subtle'),
    backgroundColor: token('elevation.surface.overlay'),
    boxShadow: `0 0 0 1px ${token('color.border.input')}, 0 2px 4px 1px ${token(
      'color.border.input'
    )}`,
    border: token('space.0'),
    cursor: 'pointer',
    zIndex: layers.navigation() + 1,
    ':hover, :focus': {
      backgroundColor: token('color.background.selected.bold'),
      color: token('color.text.inverse'),
      opacity: 1,
      transition: TRANSITION_DURATION,
    },
  },
  ({ isSidebarCollapsed }) => ({
    opacity: isSidebarCollapsed ? '1' : '0',
  })
);
